// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/team — Team & access control, after the "Team Roles, Administrative Permissions & Access Control (RBAC)" mockup)
// REAL and admin-only (both to read and to write — narrower than every other admin screen, which viewers can at least read: who has staff access at all is itself sensitive). Lists every real
// Firebase Auth account that holds a `role` claim (username, role, enabled/disabled, whether a fingerprint is enrolled — real, from `staffCredentials` — and the account's real created/last-sign-in
// time from Firebase itself, never invented). Create a new account, change a role, enable/disable one; every one of those three actions is written to a real audit log (`staffAudit`, this screen
// only — not a blockchain or every admin write in the system) and shown below. Safety rails: an admin can't change their own role or disable their own account (ask another admin), and the LAST
// enabled admin can't be demoted or disabled, so the team can never lock itself out through this screen.
// THE ROLES ARE THE SAME THREE every other admin endpoint already checks (admin/editor/viewer) — there is no finer-grained per-module permission toggle system; see the Coming-soon panel below
// for what the mockup's matrix asked for and doesn't exist. Non-admin staff never even reach this page (AdminShell + the backend both refuse them).
// Neighbours: utils/team, hooks/useAuth, components/admin/{AdminParts,Modal}, backend api/team, services/team, domain/staff_account, integrations/identity, repositories/staff_audit.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { CircleAlert, Fingerprint, Loader2, Plus, ShieldOff, UserCog } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import Modal from "../../components/admin/Modal";
import { SoonPanel, StatCard, panel } from "../../components/admin/AdminParts";
import { ApiError } from "../../utils/api";
import { formatWhen } from "../../utils/orders";
import {
  ACTION_LABEL,
  createStaffAccount,
  fetchTeam,
  fetchTeamAudit,
  MIN_PASSWORD,
  passwordProblem,
  ROLE_HINT,
  ROLE_LABEL,
  setStaffDisabled,
  setStaffRole,
  usernameProblem,
  type AuditEntry,
  type StaffAccount,
  type StaffRole,
} from "../../utils/team";

const field = "min-h-11 w-full rounded-lg bg-white px-3 text-sm ring-1 ring-ocean-900/15 dark:bg-ocean-950 dark:ring-white/20";
const btn = "inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-bold ring-1 ring-ocean-900/15 hover:bg-ocean-700/5 disabled:opacity-50 dark:ring-white/20";
const ROLES: StaffRole[] = ["admin", "editor", "viewer"];

export default function AdminTeam() {
  const auth = useAuth();
  const isAdmin = auth.user?.role === "admin";
  const [accounts, setAccounts] = useState<StaffAccount[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<StaffAccount | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [team, log] = await Promise.all([fetchTeam(), fetchTeamAudit()]);
      setAccounts(team);
      setAudit(log);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className={`${panel} flex items-start gap-3`} role="alert">
        <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-coral-600 dark:text-coral-300" aria-hidden="true" />
        <div>
          <p className="font-display text-lg font-bold">Admins only</p>
          <p className="mt-1 text-sm">Who has staff access — and changing it — is kept to Super Admins. Ask an admin if you need an account created or a role changed.</p>
        </div>
      </div>
    );
  }
  if (failed) return <p role="alert" className={`${panel} flex items-center gap-2 text-sm`}><CircleAlert className="h-4 w-4 text-coral-600" aria-hidden="true" /> Couldn't load the team. <button onClick={() => void load()} className="font-bold underline">Try again</button></p>;
  if (!accounts || !audit) return <p role="status" className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading the team…</p>;

  const byRole = (r: StaffRole) => accounts.filter((a) => a.role === r).length;
  const enabledAdmins = accounts.filter((a) => a.role === "admin" && !a.disabled).length;
  const withPasskey = accounts.filter((a) => a.hasPasskey).length;
  const disabledCount = accounts.filter((a) => a.disabled).length;

  const changeRole = async (a: StaffAccount, role: StaffRole) => {
    if (role === a.role) return;
    setBusyUid(a.uid); setError(null); setNotice("");
    try { await setStaffRole(a.uid, role); await load(); setNotice(`${a.username}'s role is now ${ROLE_LABEL[role]}.`); }
    catch (e) { setError(e instanceof ApiError ? e.message : "That didn't save."); } finally { setBusyUid(null); }
  };
  const toggleDisabled = async (a: StaffAccount) => {
    setBusyUid(a.uid); setError(null); setNotice("");
    try { await setStaffDisabled(a.uid, !a.disabled); await load(); setNotice(a.disabled ? `${a.username} can sign in again.` : `${a.username}'s access was revoked.`); setConfirmDisable(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : "That didn't save."); } finally { setBusyUid(null); }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Team &amp; access control</h1>
          <p className="max-w-3xl text-sm text-ocean-800/85 dark:text-foam-100/80">Every account with staff access, and the role it holds. Changes here take effect on that person's next sign-in.</p>
        </div>
        <button type="button" data-testid="new-staff" onClick={() => setCreating(true)} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-ocean-800 px-4 text-sm font-bold text-white"><Plus className="h-4 w-4" aria-hidden="true" /> New staff account</button>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Staff accounts" value={accounts.length} testId="kpi-total" hint={`${byRole("admin")} admin · ${byRole("editor")} editor · ${byRole("viewer")} viewer`} />
        <StatCard label="Enabled admins" value={enabledAdmins} testId="kpi-admins" hint="At least one is always kept" />
        <StatCard label="Fingerprint sign-in" value={withPasskey} testId="kpi-passkeys" hint={`of ${accounts.length} have a passkey enrolled`} />
        <StatCard label="Disabled" value={disabledCount} testId="kpi-disabled" hint="Access revoked" />
      </div>

      {notice && <p role="status" className="rounded-lg bg-teal-100 px-3 py-2 text-sm font-semibold text-teal-800">{notice}</p>}
      {error && <p role="alert" className="rounded-lg bg-coral-500/15 px-3 py-2 text-sm font-semibold text-coral-700 dark:text-coral-300">{error}</p>}

      <section aria-label="Staff directory" className={`${panel} overflow-x-auto`}>
        <table className="w-full min-w-[820px] text-left text-sm">
          <caption className="sr-only">Staff accounts with role and status</caption>
          <thead><tr className="border-b border-ocean-900/10 text-xs uppercase tracking-wide dark:border-white/10">{["Account", "Role", "Fingerprint", "Created", "Last sign-in", "Status", "Actions"].map((h) => <th key={h} scope="col" className="py-2 pr-3">{h}</th>)}</tr></thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.uid} data-testid="staff-row" className={`border-b border-ocean-900/5 align-top dark:border-white/5 ${a.disabled ? "opacity-60" : ""}`}>
                <th scope="row" className="py-3 pr-3 text-left font-bold">{a.username}{a.isSelf && <span className="ml-1.5 rounded-full bg-ocean-700/10 px-2 py-0.5 text-[11px] font-bold dark:bg-white/10">You</span>}</th>
                <td className="py-3 pr-3">
                  <select data-testid="role-select" aria-label={`Role for ${a.username}`} disabled={a.isSelf || busyUid === a.uid} value={a.role} onChange={(e) => void changeRole(a, e.target.value as StaffRole)} className={`${field} font-normal`}>
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </td>
                <td className="py-3 pr-3">{a.hasPasskey ? <span className="inline-flex items-center gap-1 text-teal-700 dark:text-teal-300"><Fingerprint className="h-4 w-4" aria-hidden="true" /> Enrolled</span> : <span className="opacity-70">—</span>}</td>
                <td className="py-3 pr-3 text-xs">{a.createdAt ? formatWhen(a.createdAt) : "—"}</td>
                <td className="py-3 pr-3 text-xs">{a.lastSignInAt ? formatWhen(a.lastSignInAt) : "Never signed in"}</td>
                <td className="py-3 pr-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${a.disabled ? "bg-coral-500/15 text-coral-700 dark:text-coral-300" : "bg-teal-100 text-teal-800"}`}>{a.disabled ? "Disabled" : "Active"}</span></td>
                <td className="py-3">
                  {a.isSelf ? <span className="text-xs opacity-70">Ask another admin</span> : (
                    <button type="button" className={btn} disabled={busyUid === a.uid} onClick={() => (a.disabled ? void toggleDisabled(a) : setConfirmDisable(a))} aria-label={`${a.disabled ? "Enable" : "Disable"} ${a.username}`}>
                      {busyUid === a.uid && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} {a.disabled ? "Enable" : "Disable"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="audit-h" className={panel}>
        <h2 id="audit-h" className="flex items-center gap-2 font-display text-lg font-bold"><UserCog className="h-5 w-5" aria-hidden="true" /> Access changes</h2>
        <p className="text-xs opacity-80">Every account created, role changed or enabled/disabled through this screen. Not a log of every admin action in the system.</p>
        {audit.length === 0 ? <p data-testid="no-audit" className="mt-3 text-sm">Nothing recorded yet.</p> : (
          <ol className="mt-3 space-y-2 text-sm">
            {audit.map((e) => (
              <li key={e.id} data-testid="audit-row" className="rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
                <span className="font-bold">{e.byUsername}</span> {ACTION_LABEL[e.action]} <span className="font-bold">{e.targetUsername}</span>{e.detail && ` (${e.detail})`}
                <span className="block text-xs opacity-80">{formatWhen(e.at)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <SoonPanel title="Granular permission matrix, geofencing & hardware key mandate" subtitle="Coming soon · not built">
        <ul className="space-y-2 text-sm">{["Per-module permission toggles (e.g. view orders but not refund)", "Per-hub / IP-subnet geofencing", "Mandatory hardware security key (FIDO2) enforcement", "Custom roles beyond admin / editor / viewer", "Cryptographically chained audit ledger"].map((r) => <li key={r} className="flex justify-between gap-3"><span>{r}</span><span className="rounded-full bg-ocean-700/10 px-2 py-0.5 text-xs font-bold dark:bg-white/10">—</span></li>)}</ul>
        <p className="mt-3 text-xs">Today's real access control is the three roles above, enforced by the backend on every admin request; the change log above is real, scoped to this screen.</p>
      </SoonPanel>

      {creating && <CreateStaffModal onClose={() => setCreating(false)} onCreated={async () => { await load(); setCreating(false); setNotice("Account created."); }} />}
      {confirmDisable && (
        <Modal title={`Disable ${confirmDisable.username}?`} onClose={() => setConfirmDisable(null)}>
          <p className="text-sm">They won't be able to sign in until an admin re-enables the account. This takes effect immediately, even on an open session.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className={btn} onClick={() => setConfirmDisable(null)}>Cancel</button>
            <button type="button" disabled={busyUid === confirmDisable.uid} onClick={() => void toggleDisabled(confirmDisable)} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-50">{busyUid === confirmDisable.uid && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Yes, disable</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CreateStaffModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<StaffRole>("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = useMemo(() => usernameProblem(username) || passwordProblem(password), [username, password]);

  const submit = async () => {
    if (problem) return;
    setBusy(true); setError(null);
    try { await createStaffAccount(username.trim().toLowerCase(), password, role); onCreated(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "That didn't save."); } finally { setBusy(false); }
  };

  return (
    <Modal title="New staff account" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-3">
        <label className="block text-xs font-bold">Username<input data-testid="new-username" className={`${field} mt-1 font-normal`} maxLength={32} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. asha" /><span className="mt-1 block font-normal opacity-80">Signs in as {username.trim().toLowerCase() || "…"}@staff.zenfresh.invalid</span></label>
        <label className="block text-xs font-bold">Password<input data-testid="new-password" type="password" className={`${field} mt-1 font-normal`} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={`At least ${MIN_PASSWORD} characters`} /></label>
        <fieldset className="text-xs font-bold">
          <legend>Role</legend>
          <div className="mt-1 space-y-1.5">
            {(["admin", "editor", "viewer"] as const).map((r) => (
              <label key={r} className="flex items-start gap-2 font-normal">
                <input type="radio" name="new-role" data-testid={`role-${r}`} checked={role === r} onChange={() => setRole(r)} className="mt-1 h-4 w-4 accent-teal-600" />
                <span><span className="font-bold">{ROLE_LABEL[r]}</span> — {ROLE_HINT[r]}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {error && <p role="alert" className="text-sm font-semibold text-coral-700 dark:text-coral-300">{error}</p>}
        {problem && username !== "" && <p role="alert" className="text-sm font-semibold text-coral-700 dark:text-coral-300">{problem}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btn}>Cancel</button>
          <button type="submit" data-testid="create-save" disabled={busy || problem !== null} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-ocean-800 px-4 text-sm font-bold text-white disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Create account</button>
        </div>
      </form>
    </Modal>
  );
}
